import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import {
	colors, Button, Card, CardContent, CardHeader,
	Divider, Typography, Grid2, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { FilePicker } from 'components';
import { enqueueSnackbar } from 'actions';
import ModalDialog from '../../../../../components/ModalDialog/ModalDialog.js';
import {
	SetFormState,
	ExportSitesByDependencies,
	UploadImportDependenciesManualData
} from '../../../actions/SiteTrackerActions.js';

const useStyles = makeStyles((theme) => ({
	root: {
		width: 600,
	},
	card: {
		marginBottom: theme.spacing(2),
	},
	cardHeader: {
		backgroundColor: colors.grey[100],
		padding: theme.spacing(1, 2),
	},
	cardContent: {
		padding: theme.spacing(2),
	},
	description: {
		marginBottom: theme.spacing(2),
		color: colors.grey[600],
	},
	resultMessage: {
		marginTop: theme.spacing(2),
		padding: theme.spacing(2),
		backgroundColor: colors.blue[50],
		borderRadius: 4,
		whiteSpace: 'pre-line',
	},
	ownerSelect: {
		marginBottom: theme.spacing(2),
	},
}));

// Owner options for the dropdown
// Each owner maps to specific columns they are responsible for
const OWNER_OPTIONS = [
	{ value: 'All', label: 'All (import all columns)' },
	{ value: 'Tulan/Paz', label: 'Tulan/Paz' },
	{ value: 'Kais', label: 'Kais' },
	{ value: 'Spencer/Howie', label: 'Spencer Bradbury & Howie Brace' },
	{ value: 'Darren/Scott', label: 'Darren Suthard / Scott Walker' },
	{ value: 'Nick Wall', label: 'Nick Wall' },
	{ value: 'JDT', label: 'JDT' },
	{ value: 'Jamie/Karthick', label: 'Jamie Easson / Karthick (E///)' },
	{ value: 'Bilal Afzal', label: 'Bilal Afzal' },
	{ value: 'Lee/Joao Cardoso', label: 'Lee Bradbury / João Cardoso (VDF)' },
	{ value: 'Joao Rebelo', label: 'João Rebelo (JDT)' },
	{ value: 'Donald/Jamie', label: 'Donald Mckernan / Jamie Anderson' },
];

const SiteTrackerExportByDependenciesDialog = (props) => {
	const { open, onClose } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.siteTrackerReducer.formState);
	const userId = useSelector(state => state.session.user.id);
	const importDependenciesManualDataMessage = useSelector((state) => state.siteTrackerReducer.importDependenciesManualDataMessage);

	// State for selected owner
	const [selectedOwner, setSelectedOwner] = useState('All');

	// Handle file selection for import
	const _onChangeInput = (event) => {
		if (event.target.files[0]?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
			if (event.target.files && event.target.files.length > 0) {
				const file = event.target.files[0];
				setFormState({
					...formState,
					values: {
						...formState.values,
						inputDependenciesManualDataPath: file
					}
				});
			}
		} else {
			dispatch(enqueueSnackbar({ message: 'File type not supported. Please select an .xlsx file.', options: { variant: 'warning' } }));
		}
	};

	// Handle owner selection change
	const _onOwnerChange = (event) => {
		setSelectedOwner(event.target.value);
	};

	// Handle Export button click
	const _onExportClick = () => {
		dispatch(ExportSitesByDependencies(userId));
	};

	// Handle Import button click
	const _onImportClick = () => {
		if (formState.values.inputDependenciesManualDataPath) {
			if (formState.values.inputDependenciesManualDataPath.size > 100000000) {
				dispatch(enqueueSnackbar({ message: 'File size cannot be superior to 100MB', options: { variant: 'warning' } }));
			} else {
				// Pass selected owner to the import action
				dispatch(UploadImportDependenciesManualData(userId, formState.values.inputDependenciesManualDataPath, selectedOwner));
			}
		} else {
			dispatch(enqueueSnackbar({ message: 'No file selected', options: { variant: 'info' } }));
		}
	};

	return (
		<ModalDialog
			maxWidth="sm"
			onClose={onClose}
			open={open}
			title="Export By Dependencies"
			hasConfirm={false}
			cancelLabel="Close"
		>
			<div style={{ padding: '16px 0' }}>
				{/* Export Section */}
				<Card className={classes.card}>
					<CardHeader
						title="Export"
						titleTypographyProps={{ variant: 'subtitle1' }}
						className={classes.cardHeader}
						avatar={<DownloadIcon color="primary" />}
					/>
					<Divider />
					<CardContent className={classes.cardContent}>
						<Typography variant="body2" className={classes.description}>
							Download the current site level data with validations (LRF, Core Data 3UK, Core Data VDF) 
							and any previously saved manual data.
						</Typography>
						<Button
							onClick={_onExportClick}
							color="primary"
							variant="contained"
							startIcon={<DownloadIcon />}
						>
							Export to Excel
						</Button>
					</CardContent>
				</Card>

				{/* Import Section */}
				<Card className={classes.card}>
					<CardHeader
						title="Import"
						titleTypographyProps={{ variant: 'subtitle1' }}
						className={classes.cardHeader}
						avatar={<UploadIcon color="secondary" />}
					/>
					<Divider />
					<CardContent className={classes.cardContent}>
						<Typography variant="body2" className={classes.description}>
							Upload an Excel file to update manual columns. Select an owner to only update their 
							specific columns, or select "All" to update all manual columns.
						</Typography>

						{/* Owner Selection Dropdown */}
						<FormControl fullWidth className={classes.ownerSelect} size="small">
							<InputLabel id="owner-select-label">Import data for</InputLabel>
							<Select
								labelId="owner-select-label"
								id="owner-select"
								value={selectedOwner}
								label="Import data for"
								onChange={_onOwnerChange}
							>
								{OWNER_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<Grid2 container spacing={1} alignItems="center">
							<Grid2 size={9}>
								<FilePicker
									placeholder="Select an .xlsx file to upload"
									accept=".xlsx"
									onChange={_onChangeInput}
									value={formState.values.inputDependenciesManualDataPath?.name || ''}
								/>
							</Grid2>
							<Grid2 size={3}>
								<Button
									onClick={_onImportClick}
									color="secondary"
									variant="contained"
									fullWidth
									startIcon={<UploadIcon />}
								>
									Import
								</Button>
							</Grid2>
						</Grid2>

						{/* Show import result message */}
						{importDependenciesManualDataMessage && (
							<div className={classes.resultMessage}>
								<Typography variant="body2">
									{importDependenciesManualDataMessage}
								</Typography>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</ModalDialog>
	);
};

SiteTrackerExportByDependenciesDialog.propTypes = {
	className: PropTypes.string,
	onClose: PropTypes.func,
	open: PropTypes.bool,
};

export default SiteTrackerExportByDependenciesDialog;